"use client";

import { MessageCircleQuestionMark as MessageCircleQuestion } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "Are your pest treatments safe for pets and children?",
  answer: "Absolutely. We use professional-grade products applied with precision. Our 'Attic to Basement' protocol ensures treatments are targeted around exterior entry points and common nesting areas, keeping your living spaces safe and sanctuary-like."
}, {
  question: "How quickly can you respond to an infestation?",
  answer: "We prioritize urgent calls. In most cases, we can have a licensed technician at your property in Durham, Raleigh, or surrounding areas within 24 to 48 hours for a comprehensive inspection."
}, {
  question: "Do you offer recurring protection plans?",
  answer: "Yes. Our 'Fortress Shield' plans provide year-round defense with quarterly visits. This proactive approach stops pests before they enter, covering everything from seasonal mosquitoes to year-round rodent prevention."
}, {
  question: "Are you licensed and insured in North Carolina?",
  answer: "We are fully licensed by the NC Department of Agriculture and carry comprehensive liability insurance. Our technicians undergo rigorous ongoing training to maintain our elite service standards."
}, {
  question: "What areas do you currently serve?",
  answer: "We provide premium pest defense across the Triangle, including Durham, Raleigh, Cary, Chapel Hill, Wake Forest, Hillsborough, and Morrisville. We are local, family-owned, and community-focused."
}, {
  question: "How do I request a free inspection?",
  answer: "You can request a quote through our website or call us directly at 919-896-1953. We'll provide a no-obligation assessment of your property's perimeter and interior."
}];
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1315" id="common-questions" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12">
      <div ref={ref} className={`mx-auto max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-[120px]">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono text-xs tracking-[0.15em] uppercase text-slate-gray">
                  04 // COMMON QUESTIONS
                </span>
                <span className="inline-block w-2 h-2 rounded-md" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>

              {}
              <h2 className="font-default font-medium text-4xl md:text-5xl leading-[1.1] tracking-tight text-foreground mb-6">
                <span>Precision answers for </span>
                <span className="relative inline-block italic font-serif text-primary">
                  <svg className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 120 60" preserveAspectRatio="none" fill="none" aria-hidden="true">
                    <path d="M 8 30 Q 4 14, 30 8 Q 60 2, 95 6 Q 116 12, 114 32 Q 110 52, 78 54 Q 40 58, 18 52 Q 2 46, 8 30 Z" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 400,
                    strokeDashoffset: isVisible ? 0 : 400,
                    transition: "stroke-dashoffset 800ms cubic-bezier(0.16, 1, 0.3, 1) 400ms",
                    transform: "rotate(-2deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>
                  <span className="relative">total</span>
                </span>
                <span> clarity.</span>
              </h2>

              <p className="font-default text-base md:text-lg text-slate-gray leading-relaxed mb-8">
                We believe in transparency and technical precision. If your specific concern isn't addressed here, our specialists are ready to provide a detailed consultation for your property.
              </p>

              {}
              <Link to="/ContactPage" className="group inline-flex items-center gap-2 font-default font-bold text-foreground uppercase tracking-widest text-sm">
                <span className="relative">
                  Need immediate help? Call us
                  <span className="absolute left-0 -bottom-0.5 h-[1px] w-0 group-hover:w-full transition-all duration-300 ease-out" style={{
                  backgroundColor: "var(--primary)"
                }} />
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1" style={{
                color: "var(--primary)"
              }}>
                  →
                </span>
              </Link>

              {}
              <div className="mt-12 hidden lg:block">
                <svg viewBox="0 0 180 220" className="w-44 h-auto" aria-hidden="true">
                  {}
                  <line x1="90" y1="30" x2="90" y2="55" stroke="var(--foreground)" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="90" cy="22" r="8" fill="var(--primary)" stroke="var(--foreground)" strokeWidth="1.5">
                    <animate attributeName="opacity" values="1;0.4;1" dur="3s" repeatCount="indefinite" />
                  </circle>
                  {}
                  <rect x="40" y="55" width="100" height="90" rx="0" fill="var(--foreground)" stroke="var(--primary)" strokeWidth="1" />
                  {}
                  <circle cx="68" cy="92" r="3" fill="var(--background)" />
                  <circle cx="112" cy="92" r="3" fill="var(--background)" />
                  {}
                  <circle cx="58" cy="108" r="4" fill="var(--primary)" opacity="0.5" />
                  <circle cx="122" cy="108" r="4" fill="var(--primary)" opacity="0.5" />
                  {}
                  <path d="M 80 120 Q 90 118, 100 120" stroke="var(--background)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  {}
                  <rect x="55" y="145" width="70" height="55" rx="0" fill="var(--background)" stroke="var(--foreground)" strokeWidth="1.5" />
                  {}
                  <path d="M 140 130 Q 155 120, 148 100 Q 142 85, 125 95" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  {}
                  <circle cx="155" cy="55" r="12" fill="transparent" stroke="var(--primary)" strokeWidth="1" />
                  <circle cx="140" cy="72" r="4" fill="var(--primary)" stroke="var(--foreground)" strokeWidth="1" />
                </svg>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="flex flex-col gap-2">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 rounded-md border border-border bg-card text-card-foreground shadow-sm transition-all duration-300 hover:border-primary/60 px-2">
                  <AccordionTrigger className="group hover:no-underline py-6 px-4 md:px-6 [&>svg]:hidden">
                    <div className="flex items-center gap-4 md:gap-6 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.15em] uppercase text-slate-gray shrink-0 w-12">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-semibold text-lg md:text-xl text-foreground leading-snug flex-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-md border border-foreground transition-all duration-300 group-data-[state=open]:rotate-45 group-data-[state=open]:bg-primary group-data-[state=open]:border-primary group-data-[state=open]:text-primary-foreground" style={{
                    backgroundColor: "transparent",
                    color: "var(--foreground)"
                  }}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M8 2 V14 M2 8 H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 md:px-6 pb-6">
                    <div className="pl-0 md:pl-[4.5rem]">
                      <p className="font-default text-base md:text-lg text-slate-gray leading-relaxed">
                        {faq.answer}
                      </p>
                      <Link to="/ContactPage" className="group/link mt-4 inline-flex items-center gap-2 font-default font-bold text-xs uppercase tracking-widest text-foreground">
                        <MessageCircleQuestion className="w-4 h-4" style={{
                      color: "var(--primary)"
                    }} />
                        <span className="relative">
                          Request Consultation
                          <span className="absolute left-0 -bottom-0.5 h-[1px] w-0 group-hover/link:w-full transition-all duration-300 ease-out" style={{
                        backgroundColor: "var(--primary)"
                      }} />
                        </span>
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}